window.JRS = window.JRS || {};

(function(JRS) {
  'use strict';

  // B4 (2026-08-02) — dual-read for customer-facing keys renamed to hidden
  // underscore form. New carts write '_Key'; carts created before the rename
  // still carry 'Key'. Readers accept both until old carts naturally clear.
  // ERP parity (2026-08-04) — position variant for slot n from the parent's
  // _customization_data JSON (positions[].variantId); null when absent.
  function _positionVariantFromCD(props, n) {
    try {
      var raw = props && props._customization_data;
      if (!raw) return null;
      var parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      var list = parsed && parsed.positions;
      if (!list || !list[n - 1]) return null;
      var v = list[n - 1].variantId;
      return (v == null) ? null : String(v);
    } catch (e) { return null; }
  }

  function _dualProp(props, key) {
    if (!props) return undefined;
    var u = props['_' + key];
    return (typeof u !== 'undefined') ? u : props[key];
  }

  // Numbered-slot read across the 2026-08-20 Tracker rename: new form first
  // ("Logo Id1"), then every pre-rename spelling ("_Logo Id 1" / "Logo Id 1").
  function _slotProp(props, base, n) {
    var v = _dualProp(props, base + ' ' + n);   // canonical (v1.1): space form
    return (typeof v !== 'undefined') ? v : _dualProp(props, base + n);
  }

  var EventBus = JRS.EventBus;
  var CartAPI = JRS.CartAPI;

  /**
   * Run immediately after a successful /cart/add.js response.
   * Uses delayed verification from CartAPI, then recovers truly missing children.
   *
   * @param {import('./jrs-v2-types.js').CartPayload} expectedPayload
   * @param {Object} addResponse
   * @returns {Promise<import('./jrs-v2-types.js').ValidationResult>}
   */
  // P0.4 (Bug T2) — Parent-qty parity check. The /cart/add.js batch can drop a unit from a parent
  // line item silently (#58765 Tee Jays 3→2). This compares expected parent quantities against the
  // current cart and uses CartAPI.changeItem to top up any deflated parent. Returns the count fixed.
  function _verifyAndFixParentQuantities(expectedPayload) {
    var expectedItems = (expectedPayload && expectedPayload.items) || [];
    var expectedParentQty = {};
    for (var i = 0; i < expectedItems.length; i++) {
      var it = expectedItems[i];
      var p = it && it.properties ? it.properties : null;
      if (p && p._ParentVariant) continue; // children skipped — handled by lineItemKey path
      var vid = Number(it && it.id);
      if (!vid) continue;
      expectedParentQty[vid] = (expectedParentQty[vid] || 0) + Number(it.quantity || 1);
    }
    if (Object.keys(expectedParentQty).length === 0) return Promise.resolve(0);

    return CartAPI.getCart().then(function(cart) {
      var cartItems = (cart && cart.items) || [];
      var fixes = [];
      Object.keys(expectedParentQty).forEach(function(vidStr) {
        var vid = Number(vidStr);
        var expected = expectedParentQty[vid];
        var matchingParents = cartItems.filter(function(ci) {
          var props = ci && ci.properties ? ci.properties : {};
          return Number(ci.variant_id) === vid && !props._ParentVariant;
        });
        var actual = matchingParents.reduce(function(sum, ci) { return sum + (ci.quantity || 0); }, 0);
        if (actual < expected && matchingParents.length > 0) {
          var deltaTarget = matchingParents[0];
          var newQty = (deltaTarget.quantity || 0) + (expected - actual);
          fixes.push({ key: deltaTarget.key, quantity: newQty, variantId: vid, expected: expected, actual: actual });
        }
      });
      if (fixes.length === 0) return 0;
      return _applyParentQtyFixesSequentially(fixes, 0).then(function() { return fixes.length; });
    });
  }

  function _applyParentQtyFixesSequentially(fixes, idx) {
    if (idx >= fixes.length) return Promise.resolve();
    var fix = fixes[idx];
    return CartAPI.changeItem(fix.key, { quantity: fix.quantity })
      .catch(function() {})
      .then(function() { return _applyParentQtyFixesSequentially(fixes, idx + 1); });
  }

  function verifyAfterAdd(expectedPayload, addResponse) {
    void addResponse;
    var startedAt = Date.now();

    return CartAPI.verifyAfterAdd(expectedPayload).then(function(verification) {
      if (verification.verified) {
        return _verifyAndFixParentQuantities(expectedPayload).then(function(parentFixCount) {
          EventBus.emit('validation:completed', {
            issuesFound: parentFixCount,
            issuesFixed: parentFixCount,
            duration: Date.now() - startedAt
          });
          var cleanResult = { valid: true, issues: [], issuesFixed: parentFixCount };
          // P1.1 — persist on every post-add validation, not just the clean-cart-page-load path.
          _persistValidationSummary(cleanResult, Date.now() - startedAt);
          return cleanResult;
        });
      }

      return CartAPI.getCart().then(function(cart) {
        var existingByLineItemKey = _buildLineItemKeyMap(cart.items || []);
        var candidates = verification.missingItems || [];
        var trulyMissing = [];

        // B1-safe: missing is decided by _lineItemKey existence, not variant_id.
        for (var i = 0; i < candidates.length; i++) {
          var candidate = candidates[i];
          var props = candidate && candidate.properties ? candidate.properties : {};
          var key = props._lineItemKey;
          if (!key) continue;
          if (!existingByLineItemKey[key]) {
            trulyMissing.push(candidate);
          }
        }

        return _reAddMissingSequentially(trulyMissing, 0).then(function() {
          return CartAPI.getCart().then(function(freshCart) {
            var freshMap = _buildLineItemKeyMap(freshCart.items || []);
            var issues = [];
            var issuesFixed = 0;

            for (var j = 0; j < trulyMissing.length; j++) {
              var item = trulyMissing[j];
              var itemProps = item && item.properties ? item.properties : {};
              var lineKey = itemProps._lineItemKey;
              if (lineKey && freshMap[lineKey]) {
                issuesFixed += 1;
              } else {
                var parentVariantId = Number(itemProps._ParentVariant || 0);
                var issue = {
                  type: 'missing',
                  parentVariantId: parentVariantId,
                  details: 'Child item still missing after recovery for _lineItemKey "' + String(lineKey || '') + '".',
                  fixed: false
                };
                issues.push(issue);
                EventBus.emit('validation:issueFound', {
                  type: issue.type,
                  parentVariantId: issue.parentVariantId,
                  details: issue.details
                });
              }
            }

            return _verifyAndFixParentQuantities(expectedPayload).then(function(parentFixCount) {
              EventBus.emit('validation:completed', {
                issuesFound: issues.length + parentFixCount,
                issuesFixed: issuesFixed + parentFixCount,
                duration: Date.now() - startedAt
              });

              var recoveryResult = {
                valid: issues.length === 0,
                issues: issues,
                issuesFixed: issuesFixed + parentFixCount
              };
              // P1.1 — persist on the recovery path too.
              _persistValidationSummary(recoveryResult, Date.now() - startedAt);
              return recoveryResult;
            });
          });
        });
      });
    });
  }

  // D-0b: per-fix validation action audit trail. Rolling in-memory buffer (capped),
  // snapshotted into the `_cart_validation_debug_log` cart attribute by
  // _persistValidationSummary. `mode` supports SHADOW entries (D-2 amendment 1):
  // a 'shadow' action records a would-remove _lineItemKey without removing anything.
  var _pendingActions = [];

  function _logValidationAction(issue, state, action) {
    try {
      issue = issue || {};
      action = action || {};
      var rec = {
        ts: new Date().toISOString(),
        type: issue.type || 'unknown',
        mode: action.mode || 'enforce',
        result: action.result || 'attempted',
        endpoint: action.endpoint || '',
        lineItemKey: issue._lineItemKey || '',
        childKey: issue._childKey || '',
        parentKey: issue._parentKey || '',
        parentVariantId: (typeof issue.parentVariantId !== 'undefined') ? issue.parentVariantId : null
      };
      var ak = Object.keys(action);
      for (var i = 0; i < ak.length; i++) {
        if (ak[i] === 'mode' || ak[i] === 'result' || ak[i] === 'endpoint') { continue; }
        rec[ak[i]] = action[ak[i]];
      }
      // Orphan issues carry only the Shopify _childKey — resolve the canonical
      // _lineItemKey (and position/qty) from the live cart for the audit record.
      if (!rec.lineItemKey && issue._childKey && state && state.cart && state.cart.items) {
        for (var li = 0; li < state.cart.items.length; li++) {
          var it = state.cart.items[li];
          if (it && String(it.key || '') === String(issue._childKey) && it.properties) {
            rec.lineItemKey = String(it.properties._lineItemKey || '');
            rec.position = String(it.properties.Position || '');
            if (typeof rec.beforeQty === 'undefined') { rec.beforeQty = it.quantity; }
            break;
          }
        }
      }
      _pendingActions.push(rec);
      if (_pendingActions.length > 100) { _pendingActions = _pendingActions.slice(-100); }
      return rec; // returned for direct unit-testing of the action schema (D-0b review item 3)
    } catch (e) { return null; }
  }

  function _persistValidationSummary(result, durationMs) {
    try {
      var nowIso = new Date().toISOString();
      var summary = JSON.stringify({
        valid: result.valid,
        issuesFound: result.issues ? result.issues.length : 0,
        issuesFixed: result.issuesFixed || 0,
        duration: durationMs,
        timestamp: nowIso
      });
      // D-0b: build the per-fix ACTION LOG (rolling window). Q12-safe (review): it is
      // written to its own key `_cart_validation_actions` below, NOT to
      // `_cart_validation_debug_log` — that key keeps a byte-copy of `summary` for
      // back-compat with the unidentified Incomplete-Caution workflow (consolidate once
      // Q12 is reported). The action log is distinct content and supports shadow entries.
      var recentActions = _pendingActions.slice(-25);
      var actionLog = JSON.stringify({ runAt: nowIso, count: recentActions.length, actions: recentActions });
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            _jrs_validation_summary: summary,
            // Q12-safe (review): the unidentified Incomplete-Caution workflow MAY parse
            // _cart_validation_debug_log, so keep the summary copy here for back-compat and put
            // the new per-fix action log on its own key. Revisit/consolidate once Q12 is reported.
            // ERP-4 hygiene (2026-08-04): was a byte-copy of _jrs_validation_summary.
            // Cleared ('' removes the attribute) — one canonical copy remains.
            _cart_validation_debug_log: '',
            _cart_validation_actions: actionLog,
            _cart_validation_actions_count: String(recentActions.length),
            _cart_validation_last_run: nowIso
          }
        })
      }).catch(function() {});
    } catch (e) {}
  }

  /**
   * Full cart integrity check. Run on cart page load.
   *
   * @param {import('./jrs-v2-types.js').ShopifyCartResponse} [cart]
   * @returns {Promise<import('./jrs-v2-types.js').ValidationResult>}
   */
  function validateCart(cart) {
    var startedAt = Date.now();

    return _resolveCart(cart).then(function(resolvedCart) {
      EventBus.emit('validation:started', { cartItemCount: (resolvedCart.items || []).length });

      var classified = _classifyCartItems(resolvedCart.items || []);
      if (classified.parents.length === 0 && classified.children.length === 0) {
        EventBus.emit('validation:completed', {
          issuesFound: 0,
          issuesFixed: 0,
          duration: Date.now() - startedAt
        });
        return { valid: true, issues: [], issuesFixed: 0 };
      }

      var issues = [];
      var issuesFixed = 0;

      var orphans = _findOrphans(classified.parents, classified.children);
      for (var i = 0; i < orphans.length; i++) {
        issues.push({
          type: 'orphan',
          parentVariantId: Number(_getParentVariantFromChild(orphans[i].child) || 0),
          details: orphans[i].reason,
          fixed: false,
          _childKey: orphans[i].child.key
        });
      }

      // Standard parent qty deflation (Bug T2 — Order #60232). Push BEFORE quantityMismatch
      // so the heal applies in the correct order (parent up first; per-child mismatches
      // below the deflation case are already skipped inside _findQuantityMismatches).
      var deflations = _findStandardParentQtyDeflation(classified.parents, classified.children);
      for (var dfi = 0; dfi < deflations.length; dfi++) {
        issues.push({
          type: 'parentQtyDeflation',
          parentVariantId: Number(deflations[dfi].parent.variant_id || 0),
          details: 'Standard parent qty deflated. Variant ' + String(deflations[dfi].parent.variant_id) + ': actual=' + deflations[dfi].actualQty + ', expected=' + deflations[dfi].expectedQty + ' (max linked child qty).',
          fixed: false,
          _parentKey: String(deflations[dfi].parent.key || ''),
          _targetQty: deflations[dfi].expectedQty
        });
      }

      // Pushed AFTER parentQtyDeflation so reconstructed children inherit the healed parent qty.
      var missingChildren = _findMissingChildren(classified.parents, classified.children);
      for (var m = 0; m < missingChildren.length; m++) {
        issues.push({
          type: 'missing',
          parentVariantId: Number(missingChildren[m].parent.variant_id || 0),
          details: 'Parent variant ' + String(missingChildren[m].parent.variant_id) + ' has missing children (expected ' + missingChildren[m].expected + ', actual ' + missingChildren[m].actual + ').',
          fixed: false
        });
      }

      var mismatches = _findQuantityMismatches(classified.parents, classified.children);
      for (var q = 0; q < mismatches.length; q++) {
        issues.push({
          type: 'quantityMismatch',
          parentVariantId: Number(mismatches[q].parent.variant_id || 0),
          details: 'Child quantity mismatch for parent variant ' + String(mismatches[q].parent.variant_id) + ': child=' + mismatches[q].childQty + ', parent=' + mismatches[q].parentQty + '.',
          fixed: false,
          _childKey: mismatches[q].child.key,
          _targetQty: mismatches[q].parentQty,
          _lineItemKey: String((mismatches[q].child.properties && mismatches[q].child.properties._lineItemKey) || '')
        });
      }

      // Bundle child drift (parent qty * _qty mismatch). See _findBundleChildQtyDrift.
      var bundleDrifts = _findBundleChildQtyDrift(classified.parents, classified.children);
      for (var bd = 0; bd < bundleDrifts.length; bd++) {
        issues.push({
          type: 'quantityMismatch',
          parentVariantId: Number(bundleDrifts[bd].parent.variant_id || 0),
          details: 'Bundle child qty drift for parent variant ' + String(bundleDrifts[bd].parent.variant_id) + ' (lineItemKey=' + bundleDrifts[bd].lineItemKey + '): child=' + bundleDrifts[bd].childQty + ', expected=' + bundleDrifts[bd].expectedQty + '.',
          fixed: false,
          _childKey: bundleDrifts[bd].child.key,
          _targetQty: bundleDrifts[bd].expectedQty,
          _lineItemKey: bundleDrifts[bd].lineItemKey,
          // Garment variant — disambiguates per-size lines sharing one _lineItemKey
          // so the stale-key re-resolve in the apply targets THIS size, not the first.
          _ProductVariantId: String((bundleDrifts[bd].child.properties && bundleDrifts[bd].child.properties._ProductVariantId) || '')
        });
      }

      var stripped = _findStrippedProperties(classified.parents);
      for (var s = 0; s < stripped.length; s++) {
        var parentVariant = Number(stripped[s].parent.variant_id || 0);
        if (_childrenForParentVariant(classified.children, parentVariant).length === 0) continue;
        issues.push({
          type: 'propertyLost',
          parentVariantId: parentVariant,
          details: 'Parent variant ' + String(parentVariant) + ' is missing required property "' + stripped[s].missingProp + '".',
          fixed: false,
          _missingProp: stripped[s].missingProp
        });
      }

      // Tracker-conformance migration (2026-08-20): a parent still carrying
      // pre-rename numbered keys ("Position 1", "_Logo Id 1") works in the
      // theme (the readers fall back), but Tracker would silently ignore those
      // names on the order. Route it through the propertyLost fix — the
      // rebuild-from-children path purges both spellings and rewrites the
      // slots under the new names.
      // v1.1 correction: the CANONICAL form is "<Base> <n>" (space, no
      // underscore — control's production spelling). Legacy = the
      // underscore-prefixed space forms AND the interim no-space forms
      // shipped 2026-08-20 before the correction.
      var LEGACY_NUMBERED = /^(_(Position|Application Method|Note|Logo Id|Logo|Logo Type) \d+|(Position|Application Method|Note|Logo Id|Logo|Logo Type)\d+)$/;
      for (var lp = 0; lp < classified.parents.length; lp++) {
        var lparent = classified.parents[lp];
        var lprops = lparent.properties || {};
        if (typeof lprops._bundle_raw !== 'undefined') continue; // bundle rebuild unsupported
        var hasLegacy = false;
        for (var lk in lprops) { if (LEGACY_NUMBERED.test(lk)) { hasLegacy = true; break; } }
        if (!hasLegacy) continue;
        var lvariant = Number(lparent.variant_id || 0);
        if (_childrenForParentVariant(classified.children, lvariant).length === 0) continue;
        var lalready = false;
        for (var li = 0; li < issues.length; li++) {
          if (issues[li].type === 'propertyLost' && issues[li].parentVariantId === lvariant) { lalready = true; break; }
        }
        if (lalready) continue;
        issues.push({
          type: 'propertyLost',
          parentVariantId: lvariant,
          details: 'Parent variant ' + String(lvariant) + ' carries pre-rename numbered property names; rebuilding to the Tracker contract.',
          fixed: false,
          _missingProp: '(legacy names)'
        });
      }

      for (var e = 0; e < issues.length; e++) {
        EventBus.emit('validation:issueFound', {
          type: issues[e].type,
          parentVariantId: issues[e].parentVariantId,
          details: issues[e].details
        });
      }

      var state = {
        cart: resolvedCart
      };

      return _applyFixesSequentially(issues, 0, state).then(function() {
        for (var fi = 0; fi < issues.length; fi++) {
          if (issues[fi].fixed) issuesFixed += 1;
        }

        EventBus.emit('validation:completed', {
          issuesFound: issues.length,
          issuesFixed: issuesFixed,
          duration: Date.now() - startedAt
        });

        var result = {
          valid: issues.length === 0,
          issues: _stripInternalIssueFields(issues),
          issuesFixed: issuesFixed
        };

        _persistValidationSummary(result, Date.now() - startedAt);

        return result;
      });
    });
  }

  /**
   * Pre-checkout read-only validation.
   *
   * @param {import('./jrs-v2-types.js').ShopifyCartResponse} [cart]
   * @returns {Promise<{ canCheckout: boolean, issues: import('./jrs-v2-types.js').ValidationIssue[] }>}
   */
  function validateBeforeCheckout(cart) {
    return _resolveCart(cart).then(function(resolvedCart) {
      // (A) FAIL-CLOSED read guard. The checkout gate runs only when the shopper
      // clicked Checkout, so a cart we cannot confirm as valid-JSON-with-items is
      // UNVERIFIABLE — never "a clean empty cart that clears checkout". Block on:
      //   • the jrs-v2-cart-api.js _fetchJSON {_raw:true} sentinel (non-JSON 200),
      //   • a missing items key, OR
      //   • a normalized EMPTY {items:[]} — at the gate an empty read means the read
      //     failed (you don't click Checkout on an empty cart; an empty read on
      //     WebKit is the Bug-W sentinel normalized away). Never reach /checkout.
      var _gateItems = resolvedCart && resolvedCart.items;
      if (!resolvedCart || resolvedCart._raw === true || !_gateItems || _gateItems.length === 0) {
        EventBus.emit('validation:checkoutBlocked', { reason: 'cart_unverifiable' });
        return { canCheckout: false, issues: [{ type: 'unverifiable', details: 'Cart could not be verified (empty or unreadable /cart.js); checkout blocked.', fixed: false }] };
      }
      var items = resolvedCart.items || [];
      var classified = _classifyCartItems(items);
      var issues = [];

      var orphans = _findOrphans(classified.parents, classified.children);
      for (var i = 0; i < orphans.length; i++) {
        issues.push({
          type: 'orphan',
          parentVariantId: Number(_getParentVariantFromChild(orphans[i].child) || 0),
          details: orphans[i].reason,
          fixed: false
        });
      }

      var missingChildren = _findMissingChildren(classified.parents, classified.children);
      for (var m = 0; m < missingChildren.length; m++) {
        issues.push({
          type: 'missing',
          parentVariantId: Number(missingChildren[m].parent.variant_id || 0),
          details: 'Parent variant ' + String(missingChildren[m].parent.variant_id) + ' has missing children (expected ' + missingChildren[m].expected + ', actual ' + missingChildren[m].actual + ').',
          fixed: false
        });
      }

      // Standard parent qty deflation (Bug T2). validateBeforeCheckout is read-only —
      // these are pushed so the checkout-clear / checkout-blocked decision below
      // accounts for them. Heal must already have run via validateCart; if a
      // deflation reaches this stage it means the heal failed or the deflation
      // happened after the most recent heal pass — block checkout in either case.
      var deflationsBC = _findStandardParentQtyDeflation(classified.parents, classified.children);
      for (var dfBC = 0; dfBC < deflationsBC.length; dfBC++) {
        issues.push({
          type: 'parentQtyDeflation',
          parentVariantId: Number(deflationsBC[dfBC].parent.variant_id || 0),
          details: 'Standard parent qty deflated. Variant ' + String(deflationsBC[dfBC].parent.variant_id) + ': actual=' + deflationsBC[dfBC].actualQty + ', expected=' + deflationsBC[dfBC].expectedQty + ' (max linked child qty).',
          fixed: false
        });
      }

      var mismatches = _findQuantityMismatches(classified.parents, classified.children);
      for (var q = 0; q < mismatches.length; q++) {
        issues.push({
          type: 'quantityMismatch',
          parentVariantId: Number(mismatches[q].parent.variant_id || 0),
          details: 'Child quantity mismatch for parent variant ' + String(mismatches[q].parent.variant_id) + ': child=' + mismatches[q].childQty + ', parent=' + mismatches[q].parentQty + '.',
          fixed: false
        });
      }

      // Bundle child drift — block checkout if it remains. Same parity as validateCart.
      var bundleDriftsBC = _findBundleChildQtyDrift(classified.parents, classified.children);
      for (var bdc = 0; bdc < bundleDriftsBC.length; bdc++) {
        issues.push({
          type: 'quantityMismatch',
          parentVariantId: Number(bundleDriftsBC[bdc].parent.variant_id || 0),
          details: 'Bundle child qty drift for parent variant ' + String(bundleDriftsBC[bdc].parent.variant_id) + ' (lineItemKey=' + bundleDriftsBC[bdc].lineItemKey + '): child=' + bundleDriftsBC[bdc].childQty + ', expected=' + bundleDriftsBC[bdc].expectedQty + '.',
          fixed: false
        });
      }

      var stripped = _findStrippedProperties(classified.parents);
      for (var s = 0; s < stripped.length; s++) {
        var pVariant = Number(stripped[s].parent.variant_id || 0);
        if (_childrenForParentVariant(classified.children, pVariant).length === 0) continue;
        issues.push({
          type: 'propertyLost',
          parentVariantId: pVariant,
          details: 'Parent variant ' + String(pVariant) + ' is missing required property "' + stripped[s].missingProp + '".',
          fixed: false
        });
      }

      var criticalIssues = [];

      for (var c = 0; c < issues.length; c++) {
        // Orphans always block. parentQtyDeflation also blocks because shipping a
        // deflated parent means the customer pays for fewer items than they
        // configured (Order #60232 fulfilment hazard) — `validateCart` should have
        // healed this; if it hasn't, do not let the user proceed silently.
        if (issues[c].type === 'orphan' || issues[c].type === 'parentQtyDeflation') {
          criticalIssues.push(issues[c]);
        }
      }

      for (var p = 0; p < classified.parents.length; p++) {
        var parent = classified.parents[p];
        var props = parent.properties || {};
        var hasCustomization = props._has_customization === 'true';
        // v8.1.1 — count THIS parent's children by instance when it carries one
        // (another instance's children must not vouch for this parent).
        var childCount = props._bundle_instance_id
          ? _childrenForBundleInstance(classified.children, String(props._bundle_instance_id)).length
          : (props._customization_instance_id
              ? _childrenForCustomInstance(classified.children, String(props._customization_instance_id)).length
              : _childrenForParentVariant(classified.children, Number(parent.variant_id || 0)).length);
        if (hasCustomization && childCount === 0) {
          criticalIssues.push({
            type: 'missing',
            parentVariantId: Number(parent.variant_id || 0),
            details: 'Parent variant ' + String(parent.variant_id) + ' has customization but zero child items.',
            fixed: false
          });
        }
        // (B) Bundle missing-child hard-block — v7.0.0 release gate.
        // A bundle parent short of its configured branding positions must NOT
        // reach /checkout: validateCart's 'missing' heal skips _isBundleParent
        // (it cannot reconstruct a bundle child), so a dropped £0 branding line
        // ships silently otherwise — confirmed live 2026-06-09 ("expected 1,
        // actual 0" → fail-open bounce). Gate strictly on _isBundleParent and
        // count THIS bundle's children by _bundle_instance_id — never
        // _has_customization (bundle parents never set it, so the rule above is
        // unreachable for bundles) and never variant_id alone (HARD RULE 1: two
        // bundles sharing a parent variant would cross-count).
        if (_isBundleParent(parent)) {
          // Detect by the FULL per-size set (Doc 2 §10.4 compound key), NOT position
          // count — a per-size removal leaves the position represented (the v7.0.0
          // detection-granularity fix). _buildMissingBundleChildren reports absent
          // children (.items) AND present-but-under-qty children (.underQty). Count
          // THIS bundle's children by _bundle_instance_id (HARD RULE 1: never
          // variant_id alone — two bundles can share a parent variant). _has_customization
          // is irrelevant for bundles (they never set it).
          var bundleInstanceId = String(props._bundle_instance_id || '');
          var bundleKids = bundleInstanceId
            ? _childrenForBundleInstance(classified.children, bundleInstanceId)
            : _childrenForParentVariant(classified.children, Number(parent.variant_id || 0));
          var shortfall = _buildMissingBundleChildren(parent, bundleKids);
          var absentCount = shortfall.items.length;
          var underQtyCount = shortfall.underQty || 0;
          if (absentCount > 0 || underQtyCount > 0) {
            var bundleMissingIssue = {
              type: 'missing',
              // Discriminator read by cart-page _onCheckoutClick (immediate block).
              bundleMissing: true,
              parentVariantId: Number(parent.variant_id || 0),
              details: 'Bundle (instance ' + (bundleInstanceId || 'unknown') + ') is short: ' + absentCount + ' missing, ' + underQtyCount + ' under-qty branding line(s).',
              fixed: false
            };
            // Surface in result.issues too (not just criticalIssues) — the cart-page
            // block message reads its reason from there, and for a multi-bundle cart
            // sharing a parent variant this is the only per-instance shortfall signal.
            issues.push(bundleMissingIssue);
            criticalIssues.push(bundleMissingIssue);
          }
        }
        if (Number(parent.quantity || 0) <= 0) {
          criticalIssues.push({
            type: 'missing',
            parentVariantId: Number(parent.variant_id || 0),
            details: 'Parent variant ' + String(parent.variant_id) + ' has invalid quantity <= 0.',
            fixed: false
          });
        }
      }

      if (criticalIssues.length > 0) {
        EventBus.emit('validation:checkoutBlocked', {
          reason: criticalIssues[0].details
        });
        return { canCheckout: false, issues: issues };
      }

      EventBus.emit('validation:checkoutCleared', {});
      return { canCheckout: true, issues: issues };
    });
  }

  /**
   * Recover stripped parent properties when possible.
   *
   * @param {import('./jrs-v2-types.js').ShopifyCartResponse} [cart]
   * @returns {Promise<{ recovered: number, failed: number }>}
   */
  function recoverProperties(cart) {
    return _resolveCart(cart).then(function(resolvedCart) {
      var state = { cart: resolvedCart };
      return _recoverPropertiesSequentially(state, 0, { recovered: 0, failed: 0 });
    });
  }

  /**
   * Classify cart items into parents, children, and plain items.
   *
   * @param {import('./jrs-v2-types.js').ShopifyCartItem[]} items
   * @returns {{ parents: Array, children: Array, plain: Array }}
   */
  function _classifyCartItems(items) {
    var parents = [];
    var children = [];
    var plain = [];

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var props = item.properties || {};

      if (typeof props._ParentVariant !== 'undefined') {
        children.push(item);
      } else if (
        typeof props._customization_data !== 'undefined' ||
        typeof props._bundle_raw !== 'undefined' ||
        props._has_customization === 'true'
      ) {
        parents.push(item);
      } else {
        plain.push(item);
      }
    }

    return {
      parents: parents,
      children: children,
      plain: plain
    };
  }

  /**
   * @param {Array} parents
   * @param {Array} children
   * @returns {Array<{ child: Object, reason: string }>}
   */
  function _childrenForCustomInstance(children, instanceId) {
    var out = [];
    for (var i = 0; i < children.length; i++) {
      var p = children[i].properties || {};
      if (String(p._customization_instance_id || '') === String(instanceId)) out.push(children[i]);
    }
    return out;
  }

  function _findOrphans(parents, children) {
    // v8.1.1 (orders #72575, #73582): a child is matched to its parent by INSTANCE
    // first. Every abandoned/re-added attempt mints a new _bundle_instance_id
    // (bundle) / _customization_instance_id (multistep); a child whose instance has
    // no parent line is an orphan even when another parent of the same variant
    // exists — that "same-variant parent exists" rule is exactly how 12 paid
    // embroidery lines from an abandoned attempt passed validation on #72575.
    // Children without an instance id (pre-§13.6 lines) fall back to _ParentVariant.
    var byVariant = {};
    var byBundleInstance = {};
    var byCustomInstance = {};
    for (var i = 0; i < parents.length; i++) {
      var pp = parents[i].properties || {};
      byVariant[String(Number(parents[i].variant_id || 0))] = true;
      if (pp._bundle_instance_id) byBundleInstance[String(pp._bundle_instance_id)] = true;
      if (pp._customization_instance_id) byCustomInstance[String(pp._customization_instance_id)] = true;
    }

    var orphans = [];
    for (var c = 0; c < children.length; c++) {
      var child = children[c];
      var cp = child.properties || {};
      var parentVariant = _getParentVariantFromChild(child);
      var key = String(Number(parentVariant || 0));
      var bInst = String(cp._bundle_instance_id || '');
      var cInst = String(cp._customization_instance_id || '');
      if (bInst) {
        if (!byBundleInstance[bInst]) {
          orphans.push({
            child: child,
            reason: 'Child item "' + String(child.key || '') + '" belongs to bundle instance ' + bInst + ' which has no parent line (abandoned attempt).'
          });
        }
        continue;
      }
      if (cInst) {
        // v8.1.2 — a multistep instance shares ONE _customization_instance_id across
        // every size (one parent per size). The instance existing is not enough: the
        // parent for THIS child's size (_ParentVariant) must exist in that instance.
        var sizeParentPresent = false;
        for (var pi2 = 0; pi2 < parents.length; pi2++) {
          var pp2 = parents[pi2].properties || {};
          if (String(pp2._customization_instance_id || '') === cInst &&
              String(Number(parents[pi2].variant_id || 0)) === key) { sizeParentPresent = true; break; }
        }
        if (!byCustomInstance[cInst]) {
          orphans.push({
            child: child,
            reason: 'Child item "' + String(child.key || '') + '" belongs to customisation instance ' + cInst + ' which has no parent line (abandoned attempt).'
          });
        } else if (!sizeParentPresent) {
          orphans.push({
            child: child,
            reason: 'Child item "' + String(child.key || '') + '" belongs to customisation instance ' + cInst + ' but its size parent (_ParentVariant=' + key + ') is gone.'
          });
        }
        continue;
      }
      if (!byVariant[key]) {
        orphans.push({
          child: child,
          reason: 'Child item "' + String(child.key || '') + '" has no matching parent for _ParentVariant=' + String(parentVariant || '') + '.'
        });
      }
    }
    return orphans;
  }

  /**
   * @param {Array} parents
   * @param {Array} children
   * @returns {Array<{ parent: Object, expected: number, actual: number }>}
   */
  function _findMissingChildren(parents, children) {
    var missing = [];

    for (var i = 0; i < parents.length; i++) {
      var parent = parents[i];
      var props = parent.properties || {};

      // Bundles: detect by the FULL per-size set (Doc 2 §10.4 compound key), NOT
      // position count. A per-size removal leaves the position represented (other
      // sizes/products of the same position survive) yet a (position×product×size)
      // child is gone — position-count missed it (the v7.0.0 detection-granularity
      // fix). _buildMissingBundleChildren does the compound-key diff; any .items ⇒
      // at least one expected per-size child is absent → flag for the heal.
      if (typeof props._bundle_raw !== 'undefined' && String(props._bundle_raw) !== '') {
        var bInstId = String(props._bundle_instance_id || '');
        var bKids = bInstId ? _childrenForBundleInstance(children, bInstId) : _childrenForParent(parent, children);
        var bRebuild = _buildMissingBundleChildren(parent, bKids);
        if (bRebuild.items.length > 0) {
          missing.push({ parent: parent, expected: bKids.length + bRebuild.items.length, actual: bKids.length });
        }
        continue;
      }

      var actual = _childrenForParent(parent, children).length;
      var expected = Number(props._customization_count || 0);
      if (!expected || expected < 0) {
        expected = _countNumberedPositions(props);
      }

      if (expected > 0 && actual < expected) {
        missing.push({
          parent: parent,
          expected: expected,
          actual: actual
        });
      }
    }

    return missing;
  }

  /**
   * @param {Array} parents
   * @param {Array} children
   * @returns {Array<{ child: Object, parent: Object, childQty: number, parentQty: number }>}
   */
  function _findQuantityMismatches(parents, children) {
    var mismatches = [];
    for (var i = 0; i < parents.length; i++) {
      var parent = parents[i];
      if (_isBundleParent(parent)) {
        // Bundle children can legitimately be parentQty * positionQty.
        // Standard 1:1 child-vs-parent validation must not run on bundles.
        // Bundle drift is detected separately by _findBundleChildQtyDrift.
        continue;
      }
      var parentQty = Number(parent.quantity || 0);
      var linkedChildren = _childrenForParent(parent, children);
      if (linkedChildren.length === 0) continue;

      // Parent-deflation case: if any linked child has quantity > parent's quantity,
      // the parent was deflated after the add (Bug T2 — Order #58765, Order #60232).
      // The fix direction must be `parent up` not `child down` so the customer keeps
      // what they originally configured. _findStandardParentQtyDeflation handles
      // that case; we skip per-child mismatches here so we don't queue a competing
      // `child down` fix that would punish the customer for the deflation.
      var maxChildQty = 0;
      for (var c = 0; c < linkedChildren.length; c++) {
        var cq = Number(linkedChildren[c].quantity || 0);
        if (cq > maxChildQty) maxChildQty = cq;
      }
      if (maxChildQty > parentQty) continue;

      for (var j = 0; j < linkedChildren.length; j++) {
        var child = linkedChildren[j];
        var childQty = Number(child.quantity || 0);
        if (childQty !== parentQty) {
          mismatches.push({
            child: child,
            parent: parent,
            childQty: childQty,
            parentQty: parentQty
          });
        }
      }
    }
    return mismatches;
  }

  /**
   * Detect non-bundle (standard customised) parents whose `quantity` has been
   * deflated below the max quantity of any of their linked children. Order
   * #60232 is the canonical case: the customer added 2 of size S and 4 of
   * size M with one print position each (so child qty == parent qty for both
   * sizes); after /cart/add.js the S parent landed at 1 and M at 2 while
   * children still reflected the originally-requested quantities. The drift
   * survived to checkout because the existing cart-page validator only ran
   * once seconds after the add and the deflation occurred later in the flow.
   *
   * Heal direction: bump the PARENT up to match max(linked child qty). That
   * recovers what the customer originally configured. Going the other way
   * (`child down to parent`) would silently delete what they ordered.
   *
   * Bundle parents are skipped — `_findBundleChildQtyDrift` handles them with
   * the parentQty * _qty rule.
   *
   * @param {Array} parents
   * @param {Array} children
   * @returns {Array<{ parent: Object, actualQty: number, expectedQty: number }>}
   */
  function _findStandardParentQtyDeflation(parents, children) {
    var deflations = [];
    for (var i = 0; i < parents.length; i++) {
      var parent = parents[i];
      if (_isBundleParent(parent)) continue;

      var parentQty = Number(parent.quantity || 0);
      var linkedChildren = _childrenForParent(parent, children);
      if (linkedChildren.length === 0) continue;

      var maxChildQty = 0;
      for (var j = 0; j < linkedChildren.length; j++) {
        var cq = Number(linkedChildren[j].quantity || 0);
        if (cq > maxChildQty) maxChildQty = cq;
      }

      if (maxChildQty > parentQty) {
        deflations.push({
          parent: parent,
          actualQty: parentQty,
          expectedQty: maxChildQty
        });
      }
    }
    return deflations;
  }

  /**
   * Detects bundle child line items whose `quantity` has drifted from the expected
   * `parentQty * _qty` value. The cart-page qty-stepper interleaves /cart/change.js
   * calls when users click ± rapidly, which can leave paid positions stranded at an
   * intermediate qty after the user scales the bundle down (Order test 2026-05-12).
   *
   * Children are scoped by `_bundle_instance_id` when both sides have it, so multiple
   * instances of the same bundle variant don't cross over.
   *
   * @param {Array} parents
   * @param {Array} children
   * @returns {Array<{ child: Object, parent: Object, childQty: number, expectedQty: number, lineItemKey: string }>}
   */
  function _findBundleChildQtyDrift(parents, children) {
    var drifts = [];
    for (var i = 0; i < parents.length; i++) {
      var parent = parents[i];
      if (!_isBundleParent(parent)) continue;

      var parentQty = Number(parent.quantity || 0);
      if (parentQty <= 0) continue;
      var parentVariantId = Number(parent.variant_id || 0);
      var parentInstanceId = parent.properties && parent.properties._bundle_instance_id
        ? String(parent.properties._bundle_instance_id) : '';

      for (var j = 0; j < children.length; j++) {
        var child = children[j];
        var props = child && child.properties ? child.properties : {};
        if (Number(props._ParentVariant || 0) !== parentVariantId) continue;

        // Scope to this specific bundle row when both sides have an instance id.
        if (parentInstanceId && props._bundle_instance_id) {
          if (String(props._bundle_instance_id) !== parentInstanceId) continue;
        }

        // Same-variant duplicate STANDARD adds: scope to this customization row
        // when both sides carry _customization_instance_id (2026-08-02).
        var pCid = (parent.properties && parent.properties._customization_instance_id)
          ? String(parent.properties._customization_instance_id) : '';
        if (pCid && props._customization_instance_id &&
            String(props._customization_instance_id) !== pCid) continue;

        var perUnitQty = parseInt(props._qty, 10);
        if (!isFinite(perUnitQty) || perUnitQty <= 0) continue; // unknown ratio — skip

        var expectedQty = parentQty * perUnitQty;
        var actualQty = Number(child.quantity || 0);
        if (actualQty !== expectedQty) {
          drifts.push({
            child: child,
            parent: parent,
            childQty: actualQty,
            expectedQty: expectedQty,
            lineItemKey: String(props._lineItemKey || '')
          });
        }
      }
    }
    return drifts;
  }

  /**
   * @param {Array} parents
   * @returns {Array<{ parent: Object, missingProp: string }>}
   */
  function _findStrippedProperties(parents) {
    var stripped = [];

    for (var i = 0; i < parents.length; i++) {
      var parent = parents[i];
      var props = parent.properties || {};
      var hasCustomizationData = typeof props._customization_data !== 'undefined' && String(props._customization_data) !== '';
      var hasBundleRaw = typeof props._bundle_raw !== 'undefined' && String(props._bundle_raw) !== '';
      var hasCustomizationFlag = props._has_customization === 'true';
      var hasNumberedPositions = _countNumberedPositions(props) > 0;
      var hasBundleInstance = typeof props._bundle_instance_id !== 'undefined' && String(props._bundle_instance_id) !== '';

      if ((hasCustomizationFlag || hasNumberedPositions) && !hasCustomizationData && !hasBundleRaw) {
        stripped.push({ parent: parent, missingProp: '_customization_data' });
      }

      if (hasBundleInstance && !hasBundleRaw) {
        stripped.push({ parent: parent, missingProp: '_bundle_raw' });
      }
    }

    return stripped;
  }

  /**
   * Rebuild standard parent properties from children. Bundle rebuild is unsupported.
   *
   * @param {Object} parent
   * @param {Object[]} matchingChildren
   * @returns {Object|null}
   */
  function _rebuildPropertiesFromChildren(parent, matchingChildren) {
    var parentProps = parent && parent.properties ? parent.properties : {};
    if (typeof parentProps._bundle_raw !== 'undefined' || typeof parentProps._bundle_instance_id !== 'undefined') {
      var themeId = String(window._bcThemeId || window._mrkThemeId || 'base');
      var themeVersion = String(window._bcThemeVersion || window._mrkThemeVersion || 'JRSBASE-V8.3.0-09102026-BYOB');
      console.warn('[' + themeVersion + '][betterconversions][' + themeId + '][CartValidator] Bundle parent property recovery not supported - _bundle_raw too complex to rebuild from children.');
      return null;
    }

    if (!matchingChildren || matchingChildren.length === 0) {
      return null;
    }

    var rebuilt = _cloneObject(parentProps);
    // Tracker-conformance purge (2026-08-20): the clone may carry numbered keys
    // in either the pre-rename form ("Position 1", "_Logo Id 1") or the new
    // form; every slot is rebuilt below, so clear both first — this is what
    // converges in-flight carts onto the new names.
    for (var pk in rebuilt) {
      if (/^_?(Position|Application Method|Note|Logo Id|Logo Type|Logo) ?\d+$/.test(pk)) delete rebuilt[pk];
    }
    // Interim-window repair (2026-08-20): a cart added while the parent briefly
    // carried a BARE un-numbered "Logo Id" gets it moved back under the
    // underscore — an indexless base beside indexed slots is undefined for
    // Tracker (break risk #3) and control never emits one on a parent.
    if (typeof rebuilt['Logo Id'] !== 'undefined') {
      if (typeof rebuilt['_Logo Id'] === 'undefined') rebuilt['_Logo Id'] = rebuilt['Logo Id'];
      delete rebuilt['Logo Id'];
    }
    var positions = [];
    var logoIdMap = {};
    var logoIds = [];

    for (var i = 0; i < matchingChildren.length; i++) {
      var child = matchingChildren[i];
      var childProps = child.properties || {};
      var logoId = String(_dualProp(childProps, 'Logo Id') || '');
      var variantId = String(childProps._logoVariant || child.variant_id || '');

      positions.push({
        position: String(_dualProp(childProps, 'Position') || ''),
        applicationMethod: String(_dualProp(childProps, 'Application Method') || ''),
        logoId: logoId,
        variantId: variantId
      });

      if (logoId && !logoIdMap[logoId]) {
        logoIdMap[logoId] = true;
        logoIds.push(logoId);
      }

      var n = i + 1;
      rebuilt['Position ' + n] = String(_dualProp(childProps, 'Position') || '');
      rebuilt['Application Method ' + n] = String(_dualProp(childProps, 'Application Method') || '');
      rebuilt['Logo Type ' + n] = String(_dualProp(childProps, 'Logo Type') || '');
      rebuilt['Logo Id ' + n] = logoId;
      rebuilt['_logoVariant' + n] = variantId;

      if (typeof _dualProp(childProps, 'Logo') !== 'undefined') {
        rebuilt['Logo ' + n] = String(_dualProp(childProps, 'Logo') || '');
      }

      if (typeof _dualProp(childProps, 'Logo Text') !== 'undefined') rebuilt['Logo Text ' + n] = String(_dualProp(childProps, 'Logo Text') || '');
      if (typeof _dualProp(childProps, 'Logo Text 2') !== 'undefined') rebuilt['_Logo Text 2 ' + n] = String(_dualProp(childProps, 'Logo Text 2') || '');
      if (typeof _dualProp(childProps, 'Logo Text 3') !== 'undefined') rebuilt['_Logo Text 3 ' + n] = String(_dualProp(childProps, 'Logo Text 3') || '');
      if (typeof _dualProp(childProps, 'Logo Text 4') !== 'undefined') rebuilt['_Logo Text 4 ' + n] = String(_dualProp(childProps, 'Logo Text 4') || '');
      if (typeof _dualProp(childProps, 'Logo Color') !== 'undefined') rebuilt['_Logo Color ' + n] = String(_dualProp(childProps, 'Logo Color') || '');
      if (typeof _dualProp(childProps, 'Logo Font') !== 'undefined') rebuilt['_Logo Font ' + n] = String(_dualProp(childProps, 'Logo Font') || '');

      // §3 (2026-08-04) — visible key, matching the builder's new contract.
      rebuilt['Note ' + n] = String(_dualProp(childProps, 'Note') || '');
    }

    rebuilt._customization_data = JSON.stringify({
      type: 'standard_product',
      positions: positions,
      logoIds: logoIds,
      parentVariantId: Number(parent.variant_id || 0),
      customizationCount: positions.length,
      timestamp: Date.now()
    });
    rebuilt._has_customization = 'true';
    rebuilt._customization_count = positions.length;
    if (!rebuilt._system_version) rebuilt._system_version = 'v2';

    return rebuilt;
  }

  /**
   * Construct missing child line items from a parent's numbered properties.
   * Inverse of `_rebuildPropertiesFromChildren`: that function reads children to write parent
   * numbered props; this one reads parent numbered props to write children.
   *
   * `_lineItemKey` format mirrors the customizer (betterconversions-customizer.js:3677–3692):
   * `<variantId>_<parentVariantId>_<logoId>_<position with /\s+/g replaced by _>`.
   *
   * `complete` is true only when every position with a key not in `existingKeys` could be
   * reconstructed with the fields fulfilment requires (Logo URL for image logos, Logo Text for
   * text logos). When false, callers should leave the related issue unfixed so downstream
   * validation handles it.
   *
   * @param {Object} parent
   * @param {Object<string, boolean>} existingKeys
   * @returns {{ items: Object[], complete: boolean }}
   */
  function _buildMissingChildrenFromParent(parent, existingKeys) {
    var props = parent && parent.properties ? parent.properties : {};
    var parentVariantId = Number(parent && parent.variant_id || 0);
    var parentQty = Number(parent && parent.quantity || 1);
    if (parentQty <= 0) parentQty = 1;
    var expectedCount = Number(props._customization_count || 0);
    if (!expectedCount || expectedCount < 0) {
      expectedCount = _countNumberedPositions(props);
    }
    if (expectedCount <= 0) return { items: [], complete: false };

    var items = [];
    var allComplete = true;
    var nowMs = Date.now();
    var keysSeen = existingKeys || {};

    for (var n = 1; n <= expectedCount; n++) {
      // ERP parity (2026-08-04): the child LINE id is the POSITION variant.
      // _logoVariant N now carries the logo PRODUCT variant (control semantics);
      // read the position variant from _customization_data, with legacy
      // fallback to _logoVariant N (which WAS the position variant pre-change).
      var logoVariantId = Number(_positionVariantFromCD(props, n) || props['_logoVariant' + n] || 0);
      var logoId = String(_slotProp(props, 'Logo Id', n) || '');
      var position = String(_slotProp(props, 'Position', n) || '');
      var applicationMethod = String(_slotProp(props, 'Application Method', n) || '');
      var logoType = String(_slotProp(props, 'Logo Type', n) || '');
      var logoUrl = _slotProp(props, 'Logo', n);
      var logoFilename = props['Logo Filename ' + n];
      var logoText = props['Logo Text ' + n];
      var logoText2 = _dualProp(props, 'Logo Text 2 ' + n);
      var logoText3 = _dualProp(props, 'Logo Text 3 ' + n);
      var logoText4 = _dualProp(props, 'Logo Text 4 ' + n);
      var logoColor = _dualProp(props, 'Logo Color ' + n);
      var logoFont = _dualProp(props, 'Logo Font ' + n);
      var note = _slotProp(props, 'Note', n);

      var positionKey = position.replace(/\s+/g, '_');
      var lineItemKeyParts = [logoVariantId, parentVariantId, logoId, positionKey].filter(function(part) {
        return part && String(part) !== '';
      });
      var lineItemKey = lineItemKeyParts.join('_');
      if (lineItemKey && keysSeen[lineItemKey]) continue;

      if (!logoVariantId || !position || !applicationMethod) {
        allComplete = false;
        continue;
      }

      var isTextLogo = String(logoType).toLowerCase().indexOf('text') !== -1;
      if (isTextLogo) {
        if (typeof logoText === 'undefined' || String(logoText) === '') {
          allComplete = false;
          continue;
        }
      } else {
        if (typeof logoUrl === 'undefined' || String(logoUrl) === '') {
          allComplete = false;
          continue;
        }
      }

      // v8.1.3 — reconstruct through the SAME builder as a normal multistep add (the
      // inline copy wrote hidden '_Position' / '_Application Method' / '_Logo Id' /
      // '_Logo Type' / '_Logo' / '_Logo Filename' and omitted 'Applied to').
      var mBuilder = (typeof JRS !== 'undefined' && JRS.CartPayloadBuilder && typeof JRS.CartPayloadBuilder.buildMultistepChildProperties === 'function')
        ? JRS.CartPayloadBuilder : null;
      if (!mBuilder) { allComplete = false; continue; } // never re-add a drifted line
      var mLogoType = String(logoType).toLowerCase();
      var mLogo = isTextLogo
        ? { type: 'text', logoId: logoId, url: null, filename: null,
            text: { lines: [String(logoText || ''), String(logoText2 || ''), String(logoText3 || ''), String(logoText4 || '')],
                    colour: String(logoColor || ''), font: String(logoFont || '') },
            note: (typeof note !== 'undefined' ? String(note) : '') }
        : { type: (mLogoType.indexOf('existing') !== -1 ? 'existing' : 'upload'), logoId: logoId, url: String(logoUrl || ''),
            filename: (typeof logoFilename !== 'undefined' ? String(logoFilename) : ''), text: null,
            note: (typeof note !== 'undefined' ? String(note) : '') };
      // 'Applied to' inputs come from the parent CART LINE itself (product_title +
      // "Colour / Size" variant_title) — the normal add derives the same label from
      // the configuration's product title, colour and size.
      var mTitle = String((parent && parent.product_title) || '');
      var mVariantBits = String((parent && parent.variant_title) || '').split(' / ');
      var mColour = mVariantBits.length > 1 ? mVariantBits[0] : '';
      var mSize = mVariantBits.length > 1 ? mVariantBits[mVariantBits.length - 1] : (mVariantBits[0] || '');
      var childProps = mBuilder.buildMultistepChildProperties({
        parentVariantId: parentVariantId,
        quantity: parentQty,
        timestamp: nowMs,
        position: { positionVariantId: logoVariantId, positionName: position, applicationMethod: applicationMethod, logo: mLogo },
        positionIndex: n - 1,
        instanceId: props._customization_instance_id ? String(props._customization_instance_id) : '',
        product: { productTitle: mTitle, colour: mColour },
        sizeEntry: { variantId: parentVariantId, size: mSize, colour: mColour, quantity: parentQty },
        lineItemKey: lineItemKey
      });

      items.push({
        id: logoVariantId,
        quantity: parentQty,
        properties: childProps
      });
    }

    return { items: items, complete: allComplete };
  }

  /**
   * (A) v7.0.0 — reconstruct missing BUNDLE child line items BYTE-EXACT from the
   * parent's `_bundle_raw` witness (Doc 2 §9 v0.5: sizes carry {qty,variantId,sku}),
   * mirroring the cart-payload builder's `buildBundlePayload` child emission
   * (base tuple + buildBundleChildCategoryProps + addChildLogoProperties). `Logo
   * Filename` comes from the parent's numbered props (upload logos only); `_time`
   * is recovered from `_bundle_instance_id` = `bundle-<timestamp>-<rand>`. Children
   * already present are skipped by the compound key `_lineItemKey|_ProductVariantId|
   * _ProductSize` (Doc 2 §10.4).
   *
   * `complete` is false (→ caller leaves the issue unfixed → the (B)+(b) checkout
   * block is the fallback) when `_bundle_raw` is the pre-v0.5 shape ({size: qty},
   * no per-size variant), unparseable, or a slot lacks the per-size variant.
   * Per-category positions reconstruct exactly; bundle-wide positions (which
   * `_bundle_raw` pins to one category) under-emit → re-verify catches it → block.
   *
   * @param {Object} parent - the bundle parent cart line (variant_id + properties)
   * @param {Object[]} existingChildren - the bundle instance's surviving children
   * @returns {{ items: Object[], complete: boolean }}
   */
  function _buildMissingBundleChildren(parent, existingChildren) {
    var props = (parent && parent.properties) || {};
    var parentVariantId = Number(parent && parent.variant_id || 0);
    var bundleInstanceId = String(props._bundle_instance_id || '');
    // v0.5.2 — the bundle multiplier. _bundle_raw / _qty are per single bundle; the
    // line quantity carries the multiplier (parent line qty, e.g. 2 after a cart-page
    // "×2"). Reconstruct/compare at qty × multiplier so a child dropped from a ×N bundle
    // heals at the right total and an under-multiplied child is flagged under-quantity.
    var parentMultiplier = Number(parent && parent.quantity) || 1;

    // _time = the build timestamp embedded in the instance id (bundle-<ts>-<rand>).
    var timeStr = '';
    var idParts = bundleInstanceId.split('-');
    if (idParts.length >= 2 && /^[0-9]+$/.test(idParts[1])) { timeStr = idParts[1]; }

    // ERP parity (2026-08-04): _bundle_raw now round-trips as a NESTED
    // STRUCTURE in the control shape (object keyed "0..", flat Product_N
    // entries, positionVariantId, string scalars). Normalize both that and the
    // legacy v2 JSON-string shape into the internal form this heal expects.
    var raw = _normalizeBundleRawForHeal(props._bundle_meta || props._bundle_raw); // meta-first (2026-08-20)
    if (!raw || !raw.length) {
      return { items: [], complete: false };
    }

    // Logo Filename lives only on the parent's numbered props (upload logos), keyed
    // by position name + logo id so each rebuilt child can recover its filename.
    var filenameByKey = {};
    var numberedCount = Number(props._customization_count || 0);
    if (!numberedCount || numberedCount < 0) { numberedCount = _countNumberedPositions(props); }
    for (var nn = 1; nn <= numberedCount; nn++) {
      var nPos = String(_slotProp(props, 'Position', nn) || '');
      var nFile = props['Logo Filename ' + nn];
      if (nPos && typeof nFile !== 'undefined' && String(nFile) !== '') {
        filenameByKey[nPos + '|' + String(_slotProp(props, 'Logo Id', nn) || '')] = String(nFile);
      }
    }

    // Surviving children → compound-key (Doc 2 §10.4) → actual qty. Mapping to qty
    // (not just presence) lets us also flag present-but-UNDER-QTY children, so the
    // shortfall covers "missing OR under-quantity" (v7.0.0 detection-granularity fix).
    var existingByKey = {};
    var ex = existingChildren || [];
    for (var xi = 0; xi < ex.length; xi++) {
      var xp = (ex[xi] && ex[xi].properties) || {};
      existingByKey[String(xp._lineItemKey || '') + '|' + String(xp._ProductVariantId || '') + '|' + String(xp._ProductSize || '')] = Number((ex[xi] && ex[xi].quantity) || 0);
    }

    var items = [];
    var complete = true;
    var underQty = 0;

    for (var ci = 0; ci < raw.length; ci++) {
      var cat = raw[ci] || {};
      var catName = String(cat.name || '');
      var catIndex = String(typeof cat.categoryIndex !== 'undefined' ? cat.categoryIndex : '');
      var productMap = cat.products || {};
      var handles = [];
      for (var hk in productMap) { if (productMap.hasOwnProperty(hk)) { handles.push(hk); } }
      // v8.1.3 — _bundle_raw holds one Product_N row per (product, size) and no handles,
      // so it cannot reproduce the builder's `_BundleCategoryProducts` (distinct handles)
      // and `_BundleCategoryProductCount` (distinct products). Take them verbatim from any
      // surviving sibling child of the same category (byte-identical); only when the whole
      // category is gone fall back to distinct productIds / names.
      var siblingEnrich = null;
      for (var sx = 0; sx < ex.length; sx++) {
        var sp = (ex[sx] && ex[sx].properties) || {};
        if (String(sp._BundleCategoryIndex || '') === catIndex && typeof sp._BundleCategoryProducts !== 'undefined') {
          siblingEnrich = { products: String(sp._BundleCategoryProducts || ''), count: String(sp._BundleCategoryProductCount || '') };
          break;
        }
      }
      var distinctProducts = {};
      var distinctOrder = [];
      for (var dh = 0; dh < handles.length; dh++) {
        var dp = productMap[handles[dh]] || {};
        var dKey = String(dp.productId || dp.name || handles[dh]);
        if (!distinctProducts[dKey]) { distinctProducts[dKey] = String(dp.handle || dp.productHandle || dp.name || handles[dh]); distinctOrder.push(dKey); }
      }
      var productsCsv = siblingEnrich ? siblingEnrich.products : distinctOrder.map(function (k) { return distinctProducts[k]; }).join(',');
      var productCount = siblingEnrich ? siblingEnrich.count : String(distinctOrder.length);
      var positions = cat.positions || [];

      for (var pi = 0; pi < positions.length; pi++) {
        var pos = positions[pi] || {};
        // positionVariantId (control shape) is authoritative; legacy v2 carts
        // stored the position variant in logoVariantId.
        var positionVariantId = Number(pos.positionVariantId || pos.logoVariantId || 0);
        var logoId = String(pos['Logo Id'] || (pos.logo && pos.logo.logoId) || '');
        var positionName = String(pos.name || '');
        var applicationMethod = String(pos['Application Method'] || pos.applicationMethod || '');
        var logoTypeDisplay = String(pos['Logo Type'] || pos.logoType || '');
        var logoUrl = (typeof pos['Logo'] !== 'undefined') ? String(pos['Logo'] || '') : String((pos.logo && pos.logo.url) || '');
        var lt = logoTypeDisplay.toLowerCase();
        var isText = lt.indexOf('text') !== -1;
        var isUpload = lt.indexOf('upload') !== -1;
        var filename = filenameByKey[positionName + '|' + logoId];
        var lineItemKey = positionVariantId + '_' + parentVariantId + '_' + logoId + '_' + positionName.replace(/\s+/g, '_');

        for (var hi = 0; hi < handles.length; hi++) {
          var product = productMap[handles[hi]] || {};
          var productColor = String(product.color || '');
          var sizesObj = product.sizes || {};
          for (var sizeKey in sizesObj) {
            if (!sizesObj.hasOwnProperty(sizeKey)) { continue; }
            var sizeEntry = sizesObj[sizeKey];
            // Pre-v0.5 shape was {size: qty} (a number) — cannot rebuild byte-exact.
            if (!sizeEntry || typeof sizeEntry !== 'object') { complete = false; continue; }
            // v0.5.2 — the witness now keys sizes by variantId; `size` lives in the value.
            // Fall back to the key for pre-v0.5.2 (size-keyed) carts.
            var size = String(sizeEntry.size || sizeKey);
            var qty = Number(sizeEntry.qty || 0);
            if (qty <= 0) { continue; }
            var needed = qty * parentMultiplier; // v0.5.2 — expected line qty = per-bundle × multiplier
            var productVariantId = String(sizeEntry.variantId || '');
            var compoundKey = lineItemKey + '|' + productVariantId + '|' + size;
            if (Object.prototype.hasOwnProperty.call(existingByKey, compoundKey)) {
              if (existingByKey[compoundKey] < needed) { underQty++; } // present but below expected (×multiplier) qty
              continue;
            }
            if (!positionVariantId || !positionName || !applicationMethod || !sizeEntry.variantId || !timeStr) {
              complete = false;
              continue;
            }

            // v8.1.3 (order #73611) — build the reconstructed child through the SAME
            // builder as a normal add. The previous inline copy wrote hidden
            // '_Logo Id' / '_Position' / '_Application Method' / '_Logo Type' / '_Logo'
            // / '_Logo Filename' (a leading underscore hides a property from the
            // customer), omitted 'Applied to', and put the POSITION variant in
            // _logoVariant where every normal line carries the logo PRODUCT variant.
            var builder = (typeof JRS !== 'undefined' && JRS.CartPayloadBuilder && typeof JRS.CartPayloadBuilder.buildBundleChildProperties === 'function')
              ? JRS.CartPayloadBuilder : null;
            if (!builder) { complete = false; continue; } // never re-add a drifted line
            var logoObj = isText
              ? { type: 'text', logoId: logoId, url: null, filename: null,
                  text: { lines: [pos['Logo Text'] || '', pos['Logo Text 2'] || '', pos['Logo Text 3'] || '', pos['Logo Text 4'] || ''],
                          colour: pos['Logo Color'] || '', font: pos['Logo Font'] || '' } }
              : { type: (lt.indexOf('existing') !== -1 ? 'existing' : 'upload'), logoId: logoId, url: logoUrl,
                  filename: (typeof filename !== 'undefined' ? filename : ''), text: null };
            var childProps = builder.buildBundleChildProperties({
              bundleVariantId: parentVariantId,
              timeStr: timeStr,
              bundleInstanceId: bundleInstanceId,
              position: { positionVariantId: positionVariantId, positionName: positionName, applicationMethod: applicationMethod, logo: logoObj },
              category: { name: catName, index: catIndex, products: productsCsv.split(',').filter(Boolean).map(function (h) { return { productHandle: h }; }) },
              product: { productTitle: String(product.name || '').replace(/\s+/g, ' ').trim() || catName, colour: productColor },
              sizeEntry: { variantId: productVariantId, sku: String(sizeEntry.sku || ''), size: size, colour: productColor, quantity: qty },
              lineItemKey: lineItemKey,
              // the logo PRODUCT variant recorded at add time (control shape); falls back
              // to the page map / position variant exactly as the live builder does
              logoVariantId: String(pos.logoVariantId || '')
            });

            childProps['_BundleCategoryProducts'] = productsCsv;
            childProps['_BundleCategoryProductCount'] = productCount;

            items.push({ id: positionVariantId, quantity: needed, properties: childProps });
          }
        }
      }
    }

    return { items: items, complete: complete, underQty: underQty };
  }

  /**
   * @param {Object} issue
   * @returns {Object}
   */
  function _publicIssue(issue) {
    return {
      type: issue.type,
      parentVariantId: issue.parentVariantId,
      details: issue.details,
      fixed: !!issue.fixed
    };
  }

  /**
   * @param {Object[]} issues
   * @returns {Object[]}
   */
  function _stripInternalIssueFields(issues) {
    var out = [];
    for (var i = 0; i < issues.length; i++) {
      out.push(_publicIssue(issues[i]));
    }
    return out;
  }

  /**
   * @param {Object} [cart]
   * @returns {Promise<Object>}
   */
  function _resolveCart(cart) {
    if (cart && cart.items) return Promise.resolve(cart);
    return CartAPI.getCart();
  }

  /**
   * @param {Object[]} issues
   * @param {number} index
   * @param {{ cart: Object }} state
   * @returns {Promise<void>}
   */
  function _applyFixesSequentially(issues, index, state) {
    if (index >= issues.length) {
      return Promise.resolve();
    }

    var issue = issues[index];
    if (issue.type === 'orphan') {
      var orphanKey = issue._childKey;
      if (!orphanKey) return _applyFixesSequentially(issues, index + 1, state);

      return CartAPI.removeItems([orphanKey]).then(function(result) {
        _logValidationAction(issue, state, { mode: 'enforce', endpoint: '/cart/update.js#remove(0)', key: orphanKey, afterQty: 0, result: (result && result.ok) ? 'removed' : 'failed' });
        if (result && result.ok) {
          issue.fixed = true;
          return CartAPI.getCart().then(function(fresh) {
            state.cart = fresh;
            return _applyFixesSequentially(issues, index + 1, state);
          });
        }
        return _applyFixesSequentially(issues, index + 1, state);
      }).catch(function() {
        return _applyFixesSequentially(issues, index + 1, state);
      });
    }

    if (issue.type === 'parentQtyDeflation') {
      // Bump the PARENT line item up to match its tallest linked child. Heals Bug T2
      // (Order #60232) where /cart/add.js silently deflated a non-bundle parent below
      // its children. Stale-key recovery: parents on the customised flow are
      // identifiable by `(variant_id, _customization_data)` — re-resolve against the
      // freshest cart by variant + still-no-_ParentVariant before issuing the change.
      var dParentKey = issue._parentKey;
      var dTargetQty = Number(issue._targetQty || 0);
      var dParentVariantId = Number(issue.parentVariantId || 0);
      if (dParentVariantId && state.cart && Array.isArray(state.cart.items)) {
        for (var dpi = 0; dpi < state.cart.items.length; dpi++) {
          var dpItem = state.cart.items[dpi];
          if (!dpItem || !dpItem.properties) continue;
          if (dpItem.properties._ParentVariant) continue;
          if (Number(dpItem.variant_id || 0) !== dParentVariantId) continue;
          dParentKey = String(dpItem.key || '');
          break;
        }
      }
      if (!dParentKey || dTargetQty <= 0) return _applyFixesSequentially(issues, index + 1, state);

      return CartAPI.changeItem(dParentKey, { quantity: dTargetQty }).then(function(result) {
        _logValidationAction(issue, state, { mode: 'enforce', endpoint: '/cart/change.js', key: dParentKey, afterQty: dTargetQty, result: (result && result.ok) ? 'changed' : 'failed' });
        if (result && result.ok) {
          issue.fixed = true;
          return CartAPI.getCart().then(function(fresh) {
            state.cart = fresh;
            return _applyFixesSequentially(issues, index + 1, state);
          });
        }
        return _applyFixesSequentially(issues, index + 1, state);
      }).catch(function() {
        return _applyFixesSequentially(issues, index + 1, state);
      });
    }

    if (issue.type === 'quantityMismatch') {
      // MOQ is parent-only. Child fixes must always match parent quantity exactly.
      // Stale-key recovery: when multiple drifted children share a variant_id,
      // each /cart/change.js call rekeys siblings, so the captured _childKey may
      // be stale by the time we get to it. Prefer re-resolving by _lineItemKey
      // (a custom property that survives Shopify rekeys) against the freshest cart.
      var stableLineItemKey = issue._lineItemKey || '';
      var childKey = issue._childKey;
      if (stableLineItemKey) {
        // Composite re-resolve (_lineItemKey + _ProductVariantId): per-size children
        // share _lineItemKey, so a _lineItemKey-only re-resolve returns the first
        // line and the heal fixes only one size. _ProductVariantId pins THIS size;
        // _childKey stays the fallback. Both properties survive Shopify rekeys.
        var fresh = _findChildLine(state.cart, stableLineItemKey, issue._ProductVariantId);
        if (fresh && fresh.key) childKey = String(fresh.key);
      }
      var targetQty = Number(issue._targetQty || 0);
      if (!childKey) return _applyFixesSequentially(issues, index + 1, state);

      return CartAPI.changeItem(childKey, { quantity: targetQty }).then(function(result) {
        _logValidationAction(issue, state, { mode: 'enforce', endpoint: '/cart/change.js', key: childKey, afterQty: targetQty, result: (result && result.ok) ? 'changed' : 'failed' });
        if (result && result.ok) {
          issue.fixed = true;
          return CartAPI.getCart().then(function(fresh) {
            state.cart = fresh;
            return _applyFixesSequentially(issues, index + 1, state);
          });
        }
        return _applyFixesSequentially(issues, index + 1, state);
      }).catch(function() {
        return _applyFixesSequentially(issues, index + 1, state);
      });
    }

    if (issue.type === 'propertyLost') {
      var classified = _classifyCartItems((state.cart && state.cart.items) || []);
      var parent = _findParentByVariant(classified.parents, issue.parentVariantId);
      if (!parent) return _applyFixesSequentially(issues, index + 1, state);

      var matchingChildren = _childrenForParentVariant(classified.children, issue.parentVariantId);
      var rebuilt = _rebuildPropertiesFromChildren(parent, matchingChildren);
      if (!rebuilt) return _applyFixesSequentially(issues, index + 1, state);

      return CartAPI.changeItem(parent.key, { properties: rebuilt }).then(function(result) {
        _logValidationAction(issue, state, { mode: 'enforce', endpoint: '/cart/change.js#properties', key: parent.key, result: (result && result.ok) ? 'properties_restored' : 'failed' });
        if (result && result.ok) {
          issue.fixed = true;
          return CartAPI.getCart().then(function(fresh) {
            state.cart = fresh;
            return _applyFixesSequentially(issues, index + 1, state);
          });
        }
        return _applyFixesSequentially(issues, index + 1, state);
      }).catch(function() {
        return _applyFixesSequentially(issues, index + 1, state);
      });
    }

    if (issue.type === 'missing') {
      // (A) v7.0.0 HEAL-FIRST — reconstruct the missing child line items and re-add
      // them so the normal path reaches checkout with no message. Source by flow:
      //   bundle    → _bundle_raw per-size witness (Doc 2 §9 v0.5) + parent numbered
      //               props (Logo Filename) + _time from the instance id (byte-exact).
      //   multistep → parent numbered properties.
      // _customization_data is NOT used (omits Logo URL/Filename/Text). If reconstruction
      // is incomplete the issue stays unfixed → the (B)+(b) checkout block is the fallback.
      var classifiedNow = _classifyCartItems((state.cart && state.cart.items) || []);
      var parentForMissing = _findParentByVariant(classifiedNow.parents, issue.parentVariantId);
      if (!parentForMissing) return _applyFixesSequentially(issues, index + 1, state);

      var isBundleHeal = _isBundleParent(parentForMissing);
      var healInstanceId = isBundleHeal ? String((parentForMissing.properties || {})._bundle_instance_id || '') : '';
      var rebuildResult;
      if (isBundleHeal) {
        var existingBundleNow = healInstanceId
          ? _childrenForBundleInstance(classifiedNow.children, healInstanceId)
          : _childrenForParentVariant(classifiedNow.children, issue.parentVariantId);
        rebuildResult = _buildMissingBundleChildren(parentForMissing, existingBundleNow);
      } else {
        var existingChildrenNow = _childrenForParentVariant(classifiedNow.children, issue.parentVariantId);
        var existingKeysNow = {};
        for (var ekI = 0; ekI < existingChildrenNow.length; ekI++) {
          var ekProps = existingChildrenNow[ekI].properties || {};
          if (ekProps._lineItemKey) existingKeysNow[String(ekProps._lineItemKey)] = true;
        }
        rebuildResult = _buildMissingChildrenFromParent(parentForMissing, existingKeysNow);
      }

      if (!rebuildResult || !rebuildResult.items || rebuildResult.items.length === 0) {
        return _applyFixesSequentially(issues, index + 1, state);
      }

      return _reAddMissingSequentially(rebuildResult.items, 0).then(function() {
        return CartAPI.getCart().then(function(freshCart) {
          state.cart = freshCart;
          var freshClassified = _classifyCartItems(freshCart.items || []);

          if (isBundleHeal) {
            // Re-verify by reconstructing against the FRESH cart: zero still-missing
            // children ⇒ the heal restored the bundle. Otherwise leave unfixed so the
            // (B)+(b) block stops the order (never a silent partial bundle).
            var freshParentB = _findParentByVariant(freshClassified.parents, issue.parentVariantId);
            var freshBundleNow = healInstanceId
              ? _childrenForBundleInstance(freshClassified.children, healInstanceId)
              : _childrenForParentVariant(freshClassified.children, issue.parentVariantId);
            var stillMissing = freshParentB ? _buildMissingBundleChildren(freshParentB, freshBundleNow) : null;
            if (rebuildResult.complete && stillMissing && stillMissing.items && stillMissing.items.length === 0) {
              issue.fixed = true;
            }
            _logValidationAction(issue, state, { mode: 'enforce', endpoint: '/cart/add.js#reAdd', flow: 'bundle', reAddedItems: rebuildResult.items.length, stillMissing: (stillMissing && stillMissing.items) ? stillMissing.items.length : -1, result: issue.fixed ? 'reAdded' : 'partial' });
            return _applyFixesSequentially(issues, index + 1, state);
          }

          var freshChildren = _childrenForParentVariant(freshClassified.children, issue.parentVariantId);
          var freshParent = _findParentByVariant(freshClassified.parents, issue.parentVariantId);
          var expectedCount = 0;
          if (freshParent) {
            var freshProps = freshParent.properties || {};
            expectedCount = Number(freshProps._customization_count || 0);
            if (!expectedCount || expectedCount < 0) {
              expectedCount = _countNumberedPositions(freshProps);
            }
          }
          if (expectedCount > 0 && freshChildren.length >= expectedCount && rebuildResult.complete) {
            issue.fixed = true;
          }
          _logValidationAction(issue, state, { mode: 'enforce', endpoint: '/cart/add.js#reAdd', reAddedItems: (rebuildResult.items ? rebuildResult.items.length : 0), expectedCount: expectedCount, actualChildren: freshChildren.length, result: issue.fixed ? 'reAdded' : 'partial' });
          return _applyFixesSequentially(issues, index + 1, state);
        });
      }).catch(function() {
        return _applyFixesSequentially(issues, index + 1, state);
      });
    }

    return _applyFixesSequentially(issues, index + 1, state);
  }

  /**
   * @param {{ cart: Object }} state
   * @param {number} index
   * @param {{ recovered: number, failed: number }} counts
   * @returns {Promise<{ recovered: number, failed: number }>}
   */
  function _recoverPropertiesSequentially(state, index, counts) {
    var classified = _classifyCartItems((state.cart && state.cart.items) || []);
    var parents = classified.parents;
    if (index >= parents.length) {
      return Promise.resolve(counts);
    }

    var parent = parents[index];
    var parentVariant = Number(parent.variant_id || 0);
    var children = _childrenForParentVariant(classified.children, parentVariant);
    var props = parent.properties || {};
    var stripped = _findStrippedProperties([parent]);
    var isStripped = stripped.length > 0;

    if (!isStripped || children.length === 0) {
      return _recoverPropertiesSequentially(state, index + 1, counts);
    }

    var rebuilt = _rebuildPropertiesFromChildren(parent, children);
    if (!rebuilt) {
      counts.failed += 1;
      return _recoverPropertiesSequentially(state, index + 1, counts);
    }

    return CartAPI.changeItem(parent.key, { properties: rebuilt }).then(function(result) {
      if (result && result.ok) {
        counts.recovered += 1;
        return CartAPI.getCart().then(function(fresh) {
          state.cart = fresh;
          return _recoverPropertiesSequentially(state, index + 1, counts);
        });
      }
      counts.failed += 1;
      return _recoverPropertiesSequentially(state, index + 1, counts);
    }).catch(function() {
      counts.failed += 1;
      return _recoverPropertiesSequentially(state, index + 1, counts);
    });
  }

  /**
   * @param {Object[]} missingItems
   * @param {number} index
   * @returns {Promise<void>}
   */
  function _reAddMissingSequentially(missingItems, index) {
    if (index >= missingItems.length) {
      return Promise.resolve();
    }

    return CartAPI.addItems({ items: [missingItems[index]] })
      .catch(function() {
        return { ok: false };
      })
      .then(function() {
        return _reAddMissingSequentially(missingItems, index + 1);
      });
  }

  /**
   * @param {Object[]} items
   * @returns {Object<string, boolean>}
   */
  function _buildLineItemKeyMap(items) {
    var map = {};
    for (var i = 0; i < items.length; i++) {
      var props = items[i] && items[i].properties ? items[i].properties : {};
      var key = props._lineItemKey;
      if (key) map[String(key)] = true;
    }
    return map;
  }

  /**
   * Find a cart line item by its stable `_lineItemKey` custom property.
   * Shopify regenerates Shopify's `item.key` on quantity changes, but `_lineItemKey`
   * is a custom property set at add-time and stays stable across cart mutations.
   *
   * @param {Object|null} cart
   * @param {string} lineItemKey
   * @returns {Object|null}
   */
  function _findItemByLineItemKey(cart, lineItemKey) {
    if (!cart || !Array.isArray(cart.items) || !lineItemKey) return null;
    var target = String(lineItemKey);
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (!it || !it.properties) continue;
      if (String(it.properties._lineItemKey || '') === target) return it;
    }
    return null;
  }

  // Resolve ONE bundle child line by the composite (_lineItemKey, _ProductVariantId).
  // Per-size children of a position share both _lineItemKey AND cart variant_id, so
  // _findItemByLineItemKey alone returns the first match and collapses the sizes;
  // _ProductVariantId (the garment variant) is the discriminator. Degrades to a
  // _lineItemKey-only match when no variant is supplied. Local twin of the
  // cart-page resolver (module boundary — no cross-module dependency).
  function _findChildLine(cart, lineItemKey, productVariantId) {
    if (!cart || !Array.isArray(cart.items) || !lineItemKey) return null;
    var target = String(lineItemKey);
    var pv = String(productVariantId || '');
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (!it || !it.properties) continue;
      if (String(it.properties._lineItemKey || '') !== target) continue;
      if (pv && String(it.properties._ProductVariantId || '') !== pv) continue;
      return it;
    }
    return null;
  }

  /**
   * @param {Object} child
   * @returns {number}
   */
  function _getParentVariantFromChild(child) {
    var props = child && child.properties ? child.properties : {};
    return Number(props._ParentVariant || 0);
  }

  /**
   * @param {Object[]} children
   * @param {number} parentVariantId
   * @returns {Object[]}
   */
  function _childrenForParentVariant(children, parentVariantId) {
    var matches = [];
    for (var i = 0; i < children.length; i++) {
      if (Number(_getParentVariantFromChild(children[i])) === Number(parentVariantId)) {
        matches.push(children[i]);
      }
    }
    return matches;
  }

  // (B) v7.0.0 release gate — count a bundle's children by _bundle_instance_id,
  // NOT by parent variant_id. Two bundles in one cart can share a parent variant
  // (HARD RULE 1: identity must never key on variant_id alone), so the
  // checkout-block child count must be per-instance to avoid cross-bundle
  // miscounting. Bundle children carry _bundle_instance_id (confirmed in the live
  // /cart.js capture, 2026-06-09).
  function _childrenForBundleInstance(children, instanceId) {
    var matches = [];
    var want = String(instanceId || '');
    if (!want) return matches;
    for (var i = 0; i < children.length; i++) {
      var props = (children[i] && children[i].properties) || {};
      if (String(props._bundle_instance_id || '') === want) {
        matches.push(children[i]);
      }
    }
    return matches;
  }

  /**
   * @param {Object[]} parents
   * @param {number} variantId
   * @returns {Object|null}
   */
  function _findParentByVariant(parents, variantId) {
    for (var i = 0; i < parents.length; i++) {
      if (Number(parents[i].variant_id || 0) === Number(variantId)) {
        return parents[i];
      }
    }
    return null;
  }

  /**
   * @param {Object<string, any>} props
   * @returns {number}
   */
  function _countNumberedPositions(props) {
    var maxIndex = 0;
    var keys = Object.keys(props || {});
    for (var i = 0; i < keys.length; i++) {
      var match = keys[i].match(/^Position ?(\d+)$/); // canonical space form; interim no-space carts still count
      if (!match) continue;
      var n = Number(match[1]);
      if (n > maxIndex) maxIndex = n;
    }
    return maxIndex;
  }

  /**
   * @param {string} bundleRaw
   * @returns {number}
   */
  // ERP parity (2026-08-04) — normalize _bundle_raw (control nested-hash shape
  // OR legacy v2 JSON-string shape) into the category list the bundle heal
  // consumes: [{name, categoryIndex, products:{key:{name,color,sizes:{vid:{size,qty,variantId,sku}}}}, positions:[...]}]
  function _coerceRawList(x) {
    if (Array.isArray(x)) return x;
    if (x && typeof x === 'object') {
      return Object.keys(x).sort(function(a, b) { return Number(a) - Number(b); })
        .map(function(k) { return x[k]; });
    }
    return [];
  }

  function _normalizeBundleRawForHeal(rawVal) {
    var parsed = rawVal;
    if (typeof rawVal === 'string') {
      try { parsed = JSON.parse(rawVal); } catch (e) { return null; }
    }
    if (!parsed || typeof parsed !== 'object') return null;
    var cats = _coerceRawList(parsed);
    var out = [];
    for (var i = 0; i < cats.length; i++) {
      var cat = cats[i];
      if (!cat || typeof cat !== 'object') continue;
      var norm = {
        name: cat.name,
        categoryIndex: (typeof cat.categoryIndex !== 'undefined') ? cat.categoryIndex : i,
        products: {},
        positions: _coerceRawList(cat.positions)
      };
      var prods = cat.products || {};
      for (var pk in prods) {
        if (!Object.prototype.hasOwnProperty.call(prods, pk)) continue;
        var e = prods[pk];
        if (!e || typeof e !== 'object') continue;
        if (e.sizes && typeof e.sizes === 'object') {
          // legacy v2 shape — already a per-variant sizes map
          norm.products[pk] = e;
        } else {
          // control shape — flat Product_N entry (one size each); synthesize
          // the per-variant sizes map the heal's per-size loop expects
          var vid = String(e.variantId || pk);
          var sizesObj = {};
          sizesObj[vid] = {
            size: e.size || '',
            qty: Number(e.quantity) || 0,
            variantId: Number(e.variantId) || 0,
            sku: e.sku || ''
          };
          norm.products[pk] = { name: e.name, color: e.color || '', sizes: sizesObj };
        }
      }
      out.push(norm);
    }
    return out;
  }

  function _countBundlePositions(bundleRaw) {
    try {
      var parsed = bundleRaw;
      if (typeof bundleRaw === 'string') {
        parsed = JSON.parse(bundleRaw);
      }
      if (!parsed) return 0;
      if (!Array.isArray(parsed) && typeof parsed === 'object') {
        parsed = Object.keys(parsed).map(function(key) { return parsed[key]; });
      }
      if (!parsed || !parsed.length) return 0;
      var total = 0;
      for (var i = 0; i < parsed.length; i++) {
        var positions = parsed[i] && parsed[i].positions ? parsed[i].positions : [];
        if (!Array.isArray(positions) && positions && typeof positions === 'object') {
          positions = Object.keys(positions).map(function(key) { return positions[key]; });
        }
        total += positions.length;
      }
      return total;
    } catch (error) {
      var themeId = String(window._bcThemeId || window._mrkThemeId || 'base');
      var themeVersion = String(window._bcThemeVersion || window._mrkThemeVersion || 'JRSBASE-V8.3.0-09102026-BYOB');
      console.warn('[' + themeVersion + '][betterconversions][' + themeId + '][CartValidator] Failed to parse _bundle_raw for expected position count.', error);
      return 0;
    }
  }

  /**
   * @param {Object} parent
   * @returns {boolean}
   */
  function _isBundleParent(parent) {
    var props = parent && parent.properties ? parent.properties : {};
    return !!(
      (typeof props._bundle_raw !== 'undefined' && String(props._bundle_raw) !== '') ||
      (typeof props._bundle_instance_id !== 'undefined' && String(props._bundle_instance_id) !== '') ||
      (typeof props._bundle !== 'undefined' && String(props._bundle) !== '')
    );
  }

  /**
   * Row-scoped child lookup.
   * Prefer _ParentKey when present, fallback to variant mapping.
   *
   * UNIVERSAL EQUALITY-KEY RULE (P0.2 Bug S, P0.5 Bug F): any equality, dedup, cleanup, or merge of
   * decoration child line items MUST use `_lineItemKey` directly OR the full property tuple
   * `(variant_id, _ParentVariant, Logo Id, Position, Application Method)`. Never `variant_id` alone.
   *
   * @param {Object} parent
   * @param {Object[]} children
   * @returns {Object[]}
   */
  function _childrenForParent(parent, children) {
    var parentKey = String(parent && parent.key || '');
    var parentVariant = Number(parent && parent.variant_id || 0);
    var parentInstanceId = parent && parent.properties && parent.properties._bundle_instance_id
      ? String(parent.properties._bundle_instance_id) : '';
    var byParentKey = [];
    var byBundleInstance = [];
    var variantFallback = [];
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      var props = child && child.properties ? child.properties : {};
      var childParentVariant = Number(props._ParentVariant || 0);
      if (childParentVariant !== parentVariant) continue;
      if (String(props._ParentKey || '') === parentKey) {
        byParentKey.push(child);
      }
      // Multi-bundle scoping: when parent and child both carry _bundle_instance_id,
      // it's the unique stable identifier for THIS bundle add. Prefer it over the
      // variant-only fallback so two bundles sharing a variant_id don't see each
      // other's children in `_findMissingChildren` etc.
      if (parentInstanceId && props._bundle_instance_id && String(props._bundle_instance_id) === parentInstanceId) {
        byBundleInstance.push(child);
      }
      variantFallback.push(child);
    }
    if (byParentKey.length > 0) return byParentKey;
    if (byBundleInstance.length > 0) return byBundleInstance;
    if (variantFallback.length > 0 && typeof window !== 'undefined' && window._jrsCartValidatorDebug) {
      // P0.2 (Bug S) — surface variant-only fallback so cross-product cross-talk is observable in QA.
      try { console.warn('[CartValidator] _childrenForParent variant-only fallback fired; cross-talk risk', { parentKey: parentKey, parentVariant: parentVariant, fallbackCount: variantFallback.length }); } catch (e) {}
    }
    return variantFallback;
  }

  /**
   * @param {Object} obj
   * @returns {Object}
   */
  function _cloneObject(obj) {
    var source = obj || {};
    var out = {};
    var keys = Object.keys(source);
    for (var i = 0; i < keys.length; i++) {
      out[keys[i]] = source[keys[i]];
    }
    return out;
  }

  JRS.CartValidator = {
    verifyAfterAdd: verifyAfterAdd,
    validateCart: validateCart,
    validateBeforeCheckout: validateBeforeCheckout,
    // v8.1.1 test seams — instance-keyed orphan detection (orders #72575, #73582)
    _classifyItems: _classifyCartItems,
    _findOrphans: _findOrphans,
    // v8.1.3 test seams — heal reconstruction parity with the payload builder
    _buildMissingBundleChildren: _buildMissingBundleChildren,
    _buildMissingChildrenFromParent: _buildMissingChildrenFromParent,
    recoverProperties: recoverProperties,
    // D-0b: exposed so D-2 (cross-file) can record SHADOW would-remove entries into the
    // same per-fix audit buffer. Call with (issueLikeObject, stateOrNull, { mode: 'shadow', ... }).
    logAction: _logValidationAction,
    // (A) test seam — byte-exact bundle child reconstruction from _bundle_raw
    // (__tests__/cart-validator-bundle-heal.test.js asserts builder-equivalence).
    _buildMissingBundleChildren: _buildMissingBundleChildren
  };
})(window.JRS);
